import { View, Text, type StyleProp, type TextStyle } from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { colors } from "../theme/tokens";

type SectionTitleLevel = "section" | "subsection";

type Props = {
  children: string;
  icon?: LucideIcon;
  color?: string;
  /**
   * `section` (default) heads a part of the page: uppercase, light.
   * `subsection` names something inside a card or sheet, such as a group of
   * rows or a fact's label: sentence case, a darker grey than the body text
   * under it so it still reads as a title on a warm card.
   */
  level?: SectionTitleLevel;
  style?: StyleProp<TextStyle>;
};

const LEVEL_STYLES: Record<SectionTitleLevel, TextStyle> = {
  section: {
    fontSize: 12,
    fontWeight: "600",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  subsection: {
    fontSize: 13,
    fontWeight: "600",
  },
};

/**
 * The app's one title style, in two levels.
 *   - section:    12px / 600 / uppercase / textLight / letterSpacing 1.
 *   - subsection: 13px / 600 / sentence case / textMid.
 * Optional inline icon at 13px outline, same color, strokeWidth 1.75.
 */
export function SectionTitle({
  children,
  icon: Icon,
  color,
  level = "section",
  style,
}: Props) {
  const tone =
    color ?? (level === "subsection" ? colors.textMid : colors.textLight);
  const label = (
    <Text
      className="font-dm-sans"
      style={[
        { ...LEVEL_STYLES[level], color: tone },
        Icon ? { flexShrink: 1 } : null,
        style,
      ]}
    >
      {children}
    </Text>
  );

  if (!Icon) return label;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: level === "section" ? 7 : 6,
      }}
    >
      <Icon size={13} color={tone} strokeWidth={1.75} />
      {label}
    </View>
  );
}

export default SectionTitle;
